// components/admin/InfoEditor.jsx
import React, { useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { infoService } from '../_services/infoService';
import { useInfo } from '../_shared/hooks/useInfo';
import './InfoEditor.css';

const InfoEditor = ({ slug }) => {
  const {
    editMode,
    htmlContent,
    pdfUrl,
    pdfFile,
    isSaving,
    isLoading,
    error,
    showPreview,
    setHtmlContent,
    setPdfFile,
    changeMode,
    handleSave,
    handleDeletePdf,
    openPreview,
    closePreview,
    setError,
  } = useInfo(slug); // ← el hook de arriba

  // Estado para el HTML raw (modo "código")
  const [rawHtml, setRawHtml] = useState(htmlContent);
  const baseUrl = process.env.REACT_APP_UPLOADS_INFO?.replace(/\/$/, '') || 'http://localhost:5000';

  

  // ==========================================
  // CAMBIAR ENTRE VISUAL ↔ HTML RAW
  // ==========================================
  const handleModeChange = (mode) => {
    if (mode === 'html') {
      setRawHtml(htmlContent);
    } else if (mode === 'visual') {
      setHtmlContent(rawHtml);
    }
    changeMode(mode);
  };

  // ==========================================
  // GUARDAR CON CONFIRMACIÓN
  // ==========================================
  const handleSaveClick = async () => {
    const success = await handleSave();
    if (success) {
      alert('✅ Información guardada correctamente');
      setError(null);
    }
  };

  // ==========================================
  // MANEJO DE PDF
  // ==========================================
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert('Solo se permiten archivos PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('El PDF no puede superar los 10MB');
      return;
    }
    setPdfFile(file);
    setError(null);
  };

  // ==========================================
  // LOADING
  // ==========================================
  if (isLoading) {
    return (
      <div className="info-editor-loading">
        <div className="info-editor-spinner"></div>
        <p>Cargando información...</p>
      </div>
    );
  }

  return (
    <div className="info-editor">
      {/* ========================================== */}
      {/* HEADER CON TABS */}
      {/* ========================================== */}
      <div className="info-editor-header">
        <h2>📄 Información del Evento</h2>
        
        <div className="info-editor-tabs">
          <button
            className={`tab-btn ${editMode === 'visual' ? 'active' : ''}`}
            onClick={() => handleModeChange('visual')}
            disabled={isSaving}
          >
            ✏️ Visual
          </button>
          <button
            className={`tab-btn ${editMode === 'html' ? 'active' : ''}`}
            onClick={() => handleModeChange('html')}
            disabled={isSaving}
          >
            💻 HTML
          </button>
          <button
            className={`tab-btn ${editMode === 'pdf' ? 'active' : ''}`}
            onClick={() => handleModeChange('pdf')}
            disabled={isSaving}
          >
            📄 PDF
          </button>
        </div>

        <div className="info-editor-actions">
          <button
            className="preview-btn"
            onClick={openPreview}
            disabled={isSaving}
          >
            👁️ Vista Previa
          </button>
          <button
            className="save-btn"
            onClick={handleSaveClick}
            disabled={isSaving}
          >
            {isSaving ? '💾 Guardando...' : '💾 Publicar'}
          </button>
        </div>
      </div>

      {/* ========================================== */}
      {/* ERROR */}
      {/* ========================================== */}
      {error && (
        <div className="info-editor-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ========================================== */}
      {/* CONTENIDO SEGÚN MODO */}
      {/* ========================================== */}
      <div className="info-editor-content">
        {/* MODO VISUAL (Quill) */}
        {editMode === 'visual' && (
          <div className="editor-visual">
            <ReactQuill
              value={htmlContent}
              onChange={setHtmlContent}
              modules={infoService.getQuillModules()}
              formats={infoService.getQuillFormats()}
              theme="snow"
              placeholder="Escribe la convocatoria del evento aquí..."
            />
          </div>
        )}

        {/* MODO HTML RAW */}
        {editMode === 'html' && (
          <div className="editor-html">
            <textarea
              className="html-textarea"
              value={rawHtml}
              onChange={(e) => setRawHtml(e.target.value)}
              placeholder="Pega o edita el código HTML aquí..."
              spellCheck={false}
            />
            <p className="html-hint">
              💡 Puedes pegar HTML directamente. Los cambios se reflejarán al volver al modo Visual.
            </p>
          </div>
        )}

        {/* MODO PDF */}
        {editMode === 'pdf' && (
          <div className="editor-pdf">
            <div className="pdf-uploader">
              <label className="pdf-upload-label">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfChange}
                  style={{ display: 'none' }}
                />
                <div className="pdf-upload-box">
                  <span className="pdf-icon">📄</span>
                  <p>
                    {pdfFile 
                      ? `✅ ${pdfFile.name}` 
                      : 'Haz clic para seleccionar un PDF'}
                  </p>
                  <small>Máximo 10MB</small>
                </div>
              </label>

              {pdfUrl && !pdfFile && (
                <div className="pdf-current">
                  <p>📎 PDF actual:</p>
                  <div className="pdf-actions">
                    <a 
                      href={pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="pdf-view-btn"
                    >
                      👁️ Ver PDF
                    </a>
                    <button 
                      className="pdf-delete-btn"
                      onClick={handleDeletePdf}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              )}

              {pdfFile && (
                <div className="pdf-pending">
                  <p>📤 PDF pendiente de subir: <strong>{pdfFile.name}</strong></p>
                  <small>Se subirá al hacer clic en "Publicar"</small>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ========================================== */}
      {/* MODAL DE PREVIEW */}
      {/* ========================================== */}
      {showPreview && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>👁️ Vista Previa</h3>
              <button className="preview-close-btn" onClick={closePreview}>
                ×
              </button>
            </div>
            <div className="preview-modal-body">
              {editMode === 'pdf' && (pdfUrl || pdfFile) ? (
                <iframe
                src={pdfFile ? URL.createObjectURL(pdfFile) : `${baseUrl}${pdfUrl}`}
                  title="Preview PDF"
                  className="preview-iframe"
                />
              ) : (
                <div 
                  className="preview-html-content"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              )}
            </div>
            <div className="preview-modal-footer">
              <button className="preview-save-btn" onClick={handleSaveClick}>
                💾 Publicar ahora
              </button>
              <button className="preview-cancel-btn" onClick={closePreview}>
                Seguir editando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoEditor;